// ===== Terminal layout: menu, toolbar, [Market Watch | Charts | Navigator], Toolbox, Status bar, resizable splitters =====
import { useEffect, useState } from "react";
import { useStore, setUI, setChart, chartOf, closeChart, newChart, getState, update } from "./store";
import MenuBar from "./MenuBar";
import Toolbar from "./Toolbar";
import MarketWatch from "./MarketWatch";
import Navigator from "./Navigator";
import Toolbox from "./Toolbox";
import StatusBar from "./StatusBar";
import Chart, { zoomChart, deleteSelected } from "./Chart";
import { startFeed, stopFeed } from "./binance";
import { startMarkets } from "./markets";
import ContextMenu, { openMenu } from "./ContextMenu";
import Dialogs, { AlertPopup } from "./Dialogs";
import DepthOfMarket from "./DepthOfMarket";
import { I } from "./Icons";
import "./mt5.css";

// Drag handle that reports pixel deltas
const Splitter = ({ dir, onDrag, testId }) => {
  const down = (e) => {
    e.preventDefault();
    let last = dir === "v" ? e.clientX : e.clientY;
    const move = (ev) => { const cur = dir === "v" ? ev.clientX : ev.clientY; onDrag(cur - last); last = cur; };
    const up = () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); document.body.style.cursor = ""; };
    document.body.style.cursor = dir === "v" ? "col-resize" : "row-resize";
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };
  return <div className={`mt-split mt-split-${dir}`} data-testid={testId} onMouseDown={down} />;
};

// Grid columns/rows for tiled chart windows
const tileStyle = (n) => {
  const cols = Math.ceil(Math.sqrt(n));
  return { gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${Math.ceil(n / cols)}, 1fr)` };
};

const ChartTabs = ({ st }) => (
  <div className="chart-tabs" data-testid="chart-tabs">
    {st.charts.map((c) => (
      <div
        key={c.id}
        className={`mt-tab ${c.id === st.activeChart ? "active" : ""}`}
        data-testid={`chart-tab-${c.id}`}
        onClick={() => setUI({ activeChart: c.id })}
        onContextMenu={(e) => openMenu(e, [{ label: "Close", onClick: () => closeChart(c.id) }, { label: "New Window", onClick: () => newChart(c.symbol, c.tf) }])}
      >
        {c.symbol},{c.tf}
        {st.charts.length > 1 && <span className="tab-x" data-testid={`chart-tab-close-${c.id}`} onClick={(e) => { e.stopPropagation(); closeChart(c.id); }}><I.close /></span>}
      </div>
    ))}
  </div>
);

export default function Terminal() {
  const st = useStore();
  const [leftW, setLeftW] = useState(340);
  const [rightW, setRightW] = useState(220);
  const [botH, setBotH] = useState(210);

  useEffect(() => { document.documentElement.dataset.theme = st.theme; }, [st.theme]);
  useEffect(() => { startMarkets(); }, []);
  useEffect(() => { st.liveFeed ? startFeed() : stopFeed(); }, [st.liveFeed]); // eslint-disable-line react-hooks/exhaustive-deps

  // keyboard shortcuts
  useEffect(() => {
    const k = (e) => {
      if (["INPUT", "SELECT", "TEXTAREA"].includes(e.target.tagName)) return;
      const c = chartOf();
      const s = getState().panels;
      if (e.key === "F9") { e.preventDefault(); setUI({ dialog: { kind: "order", symbol: c.symbol, mode: "market" } }); }
      else if (e.key === "F8") { e.preventDefault(); setUI({ dialog: { kind: "properties", chartId: c.id } }); }
      else if (e.key === "+" || e.key === "=") zoomChart(c.id, 1.3);
      else if (e.key === "-") zoomChart(c.id, 1 / 1.3);
      else if (e.ctrlKey && e.key.toLowerCase() === "z") { e.preventDefault(); setChart({ drawings: c.drawings.slice(0, -1) }); }
      else if (e.ctrlKey && e.key.toLowerCase() === "m") { e.preventDefault(); setUI({ panels: { ...s, market: !s.market } }); }
      else if (e.ctrlKey && e.key.toLowerCase() === "t") { e.preventDefault(); setUI({ panels: { ...s, toolbox: !s.toolbox } }); }
      else if (e.ctrlKey && e.key.toLowerCase() === "g") { e.preventDefault(); setChart({ grid: !c.grid }); }
      else if (e.ctrlKey && e.key.toLowerCase() === "k") { e.preventDefault(); setChart({ volumes: !c.volumes }); }
      else if (e.ctrlKey && e.key.toLowerCase() === "i") { e.preventDefault(); setUI({ dialog: { kind: "indicators", chartId: c.id } }); }
      else if (e.altKey && ["1", "2", "3"].includes(e.key)) setChart({ type: ["bars", "candles", "line"][+e.key - 1] });
      else if (e.altKey && e.key.toLowerCase() === "t") setChart({ oneClick: !c.oneClick });
      else if (e.altKey && e.key.toLowerCase() === "b") setUI({ dom: getState().dom ? null : c.symbol });
      else if (e.key === "Delete") deleteSelected(c.id);
      else if (e.key === "Escape") setUI({ tool: "cursor", dialog: null });
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  return (
    <div className="mt-root" data-testid="terminal-root" onContextMenu={(e) => e.preventDefault()}>
      <MenuBar />
      <Toolbar />
      <div className="mt-work" style={{ gridTemplateRows: st.panels.toolbox ? `1fr 4px ${botH}px` : "1fr" }}>
        <div className="mt-top">
          {st.panels.market && <div style={{ width: leftW }} className="mt-side"><MarketWatch /></div>}
          {st.panels.market && <Splitter dir="v" testId="split-left" onDrag={(d) => setLeftW((w) => clamp(w + d, 180, 600))} />}
          <div className="mt-charts" data-testid="chart-area">
            <div className={st.tile ? "chart-grid" : "chart-stack"} style={st.tile ? tileStyle(st.charts.length) : undefined} data-testid={st.tile ? "chart-grid-tiled" : "chart-grid-tabbed"}>
              {st.charts.map((c) => (
                <div key={c.id} className={`chart-holder ${st.tile && c.id === st.activeChart ? "tile-active" : ""}`} style={{ display: st.tile || c.id === st.activeChart ? "flex" : "none" }} data-testid={`chart-holder-${c.id}`}>
                  {st.tile && (
                    <div className="tile-title" data-testid={`tile-title-${c.id}`} onMouseDown={() => setUI({ activeChart: c.id })} onDoubleClick={() => update((s) => { s.tile = false; s.activeChart = c.id; })}>
                      <span>{c.symbol},{c.tf}</span>
                      <span className="tile-btns">
                        <button title="Maximize" data-testid={`tile-max-${c.id}`} onClick={() => update((s) => { s.tile = false; s.activeChart = c.id; })}>□</button>
                        {st.charts.length > 1 && <button title="Close" data-testid={`tile-close-${c.id}`} onClick={() => closeChart(c.id)}><I.close /></button>}
                      </span>
                    </div>
                  )}
                  <div className="chart-body"><Chart chartId={c.id} /></div>
                </div>
              ))}
            </div>
            <ChartTabs st={st} />
          </div>
          {st.panels.navigator && <Splitter dir="v" testId="split-right" onDrag={(d) => setRightW((w) => clamp(w - d, 150, 500))} />}
          {st.panels.navigator && <div style={{ width: rightW }} className="mt-side"><Navigator /></div>}
        </div>
        {st.panels.toolbox && <Splitter dir="h" testId="split-bottom" onDrag={(d) => setBotH((h) => clamp(h - d, 90, 600))} />}
        {st.panels.toolbox && <Toolbox />}
      </div>
      <StatusBar />
      <ContextMenu />
      <Dialogs />
      <AlertPopup />
      <DepthOfMarket />
    </div>
  );
}

